import styled from '@emotion/styled/macro';
import { BaseButton } from '../base/button/button';
import { Variables } from '../base/variables';

const V = Variables.ThemeLight;

/** The design's four cell kinds. Colours are the NS-2 pairs: pale surface, dark text from the same family. */

export const LinkText = styled.span`
    font-weight: 500;
    color: ${V.TextStrong};
`;

export const NumText = styled.span`
    font-variant-numeric: tabular-nums;
    color: ${V.TextMedium};
`;

export const PlainText = styled.span`
    color: ${V.TextMedium};
`;

/** An empty cell. Faint, so a column of dashes recedes rather than reading as data. */
export const Absent = styled.span`
    color: ${V.TextXMild};
`;

export const Chip = styled.span<{ tone: 'primary' | 'warning' }>`
    display: inline-flex;
    align-items: center;
    padding: 3px 7px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.04em;
    line-height: 1;
    white-space: nowrap;
    background: ${({ tone }) => (tone === 'primary' ? V.PrimaryMild2 : V.AttentionMild3)};
    color: ${({ tone }) => (tone === 'primary' ? V.PrimaryInverted3 : V.AttentionInverted3)};
`;

export const Empty = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 48px 16px;
    text-align: center;
`;

export const EmptyTitle = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: ${V.TextStrong};
`;

export const EmptyHint = styled.div`
    font-size: 13px;
    color: ${V.TextMild};
`;

export const Leading = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    margin-right: auto;
`;

export const Search = styled.div`
    width: 300px;
    flex: 0 1 300px;
    min-width: 180px;
`;

export const RecordCount = styled.span`
    font-size: 12px;
    white-space: nowrap;
    color: ${V.TextMild};
`;

/** The selection bar: a pale primary strip, as in the design (NS-2: pale surface, dark text). */
export const BulkBar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 14px;
    background: ${V.PrimaryMild2};
    border-bottom: 1px solid ${V.Border1};
`;

export const BulkLabel = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${V.PrimaryInverted3};
    white-space: nowrap;
`;

export const BulkActions = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-left: 4px;
`;

export const BulkButtonContent = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
`;

export const DangerButton = styled(BaseButton)`
    color: ${V.WarningInverted3};
`;

export const ClearSelection = styled.div`
    margin-left: auto;
`;

/**
 * `BaseDialog` pads only its title; content brings its own inset, as the support `ConfirmDialog` does.
 * Its content row is a flex row at least 600px wide — grow to fill it, so the inset is the same on
 * both sides and matches the title's, and stop at that 600px so long text wraps instead of
 * widening the dialog.
 */
export const DialogBody = styled.form`
    flex: 1 1 auto;
    min-width: 0;
    max-width: 600px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 0 24px 24px;
`;

export const DialogText = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${V.TextMedium};
`;

export const DialogWarning = styled.p`
    margin: 0;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1.45;
    background: ${V.AttentionMild3};
    color: ${V.AttentionInverted3};
`;

export const DialogButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
`;

/** The trailing chevron's cell — the height the support tables' actions cell had. */
export const ActionCellWrapper = styled.div`
    min-height: 40px;
    display: flex;
    align-items: center;
`;
